import { roundCents } from '@/utils/money'
import type { TaxBracket } from '@/data/tax/2027'
import {
  brackets2027,
  medicalCredits2027,
  rebates2027,
  retirement2027,
  uif2027,
} from '@/data/tax/2027'
import type { CalculatorContext } from '@/types/calculator'
import type { PayeInputs, PayeResult } from './types'

export function calculatePaye(inputs: PayeInputs, _context: CalculatorContext): PayeResult {
  const annualGross = inputs.grossPayPeriod === 'monthly' ? inputs.grossPay * 12 : inputs.grossPay
  const periodPension =
    inputs.pensionPeriod === 'monthly'
      ? inputs.pensionContribution * 12
      : inputs.pensionContribution

  const annualPensionDeduction = Math.min(
    Math.max(0, periodPension),
    retirement2027.deductionRate * annualGross,
    retirement2027.annualCap
  )

  const taxableIncome = Math.max(0, annualGross - annualPensionDeduction)

  const bracket = brackets2027.find(
    (b: TaxBracket) => taxableIncome >= b.min && (b.max === null || taxableIncome <= b.max)
  )
  if (!bracket) {
    throw new Error('Tax bracket not found')
  }

  const taxBeforeCredits = bracket.base + bracket.rate * (taxableIncome - bracket.min)

  let rebate = rebates2027.primary
  if (inputs.ageBand === '65to74') rebate += rebates2027.secondary
  if (inputs.ageBand === '75plus') rebate += rebates2027.secondary + rebates2027.tertiary

  const medicalCreditsAnnual = inputs.medicalSchemeMember
    ? medicalCredits2027.mainMember * 12 +
      (inputs.medicalDependants > 0 ? medicalCredits2027.firstDependant * 12 : 0) +
      Math.max(0, inputs.medicalDependants - 1) * medicalCredits2027.additionalDependant * 12
    : 0

  const taxAfterCredits = Math.max(0, taxBeforeCredits - rebate - medicalCreditsAnnual)

  const monthlyUifBase = Math.min(annualGross / 12, uif2027.monthlyCap)
  const uifMonthly = roundCents(monthlyUifBase * uif2027.employeeRate)
  const uifAnnual = uifMonthly * 12

  const netAnnual = annualGross - taxAfterCredits - uifAnnual - annualPensionDeduction
  const effectiveTaxRate = annualGross > 0 ? taxAfterCredits / annualGross : 0

  return {
    annualGross: roundCents(annualGross),
    monthlyGross: roundCents(annualGross / 12),
    annualPensionDeduction: roundCents(annualPensionDeduction),
    monthlyPensionDeduction: roundCents(annualPensionDeduction / 12),
    taxableIncome: roundCents(taxableIncome),
    taxBeforeCredits: roundCents(taxBeforeCredits),
    rebate: roundCents(rebate),
    medicalCreditsAnnual: roundCents(medicalCreditsAnnual),
    annualPaye: roundCents(taxAfterCredits),
    monthlyPaye: roundCents(taxAfterCredits / 12),
    uifMonthly: roundCents(uifMonthly),
    uifAnnual: roundCents(uifAnnual),
    netAnnual: roundCents(netAnnual),
    netMonthly: roundCents(netAnnual / 12),
    effectiveTaxRate,
  }
}
